import type {
  CreateHelpdeskRequestInput,
  CreateHelpdeskRequestResponse,
  HelpdeskCommentPage,
  HelpdeskDepartmentForms,
  HelpdeskExternalCatalogFormResponse,
  HelpdeskReadCursor,
  HelpdeskRequestActivityResponse,
  HelpdeskRequestBucket,
  HelpdeskRequestCancellation,
  HelpdeskRequestDetail,
  HelpdeskRequestPage,
  HelpdeskSupportResponse,
  HelpdeskUnreadCount,
  PostHelpdeskCommentInput,
  RetryHelpdeskRequestResponse,
} from "@repo/shared-types/helpdesk";
import { HELPDESK_ATTACHMENT_COMMENT_PART, HELPDESK_ATTACHMENT_FORM_PART_PREFIX } from "@repo/shared-types/helpdesk";
import { confirmedSubmission } from "../../helpdesk/web/submission-errors.ts";
import { req } from "../../shared/web/client.ts";
import { multipartConversationRequest } from "../../shared/web/linear-conversation/client.ts";

type Attachment = { fieldId: string; file: File };

export const externalRequesterApi = {
  support: (signal?: AbortSignal): Promise<HelpdeskSupportResponse> => req("/api/external/helpdesk/support", { signal }),
  activity: (requestIds: readonly string[], refresh: "stale" | "force" | "none" = "stale"): Promise<HelpdeskRequestActivityResponse> =>
    req(`/api/external/helpdesk/requests/activity?${new URLSearchParams({ requestIds: requestIds.join(","), refresh })}`),
  markAttentionRead: (requestId: string, targetId: string, episodeId: string): Promise<void> =>
    req(`/api/external/helpdesk/requests/${encodeURIComponent(requestId)}/targets/${encodeURIComponent(targetId)}/attention/read`, { method: "PUT", body: JSON.stringify({ episodeId }) }),
  markRead: (requestId: string, targetId: string, readCursor: HelpdeskReadCursor): Promise<void> =>
    req(`/api/external/helpdesk/requests/${encodeURIComponent(requestId)}/targets/${encodeURIComponent(targetId)}/read`, { method: "PUT", body: JSON.stringify({ readCursor }) }),
  unreadCount: (bucket?: HelpdeskRequestBucket, signal?: AbortSignal): Promise<HelpdeskUnreadCount> =>
    req(`/api/external/helpdesk/requests/unread-count${bucket ? `?${new URLSearchParams({ bucket })}` : ""}`, { signal }),
  requests: (bucket: HelpdeskRequestBucket, cursor: string | null, signal?: AbortSignal): Promise<HelpdeskRequestPage> =>
    req(`/api/external/helpdesk/requests?${new URLSearchParams({ bucket, ...(cursor ? { cursor } : {}) })}`, { signal }),
  cancel: (requestId: string): Promise<HelpdeskRequestCancellation> => req(`/api/external/helpdesk/requests/${encodeURIComponent(requestId)}/cancel`, { method: "POST" }),
  request: (requestId: string, signal?: AbortSignal): Promise<HelpdeskRequestDetail> => req(`/api/external/helpdesk/requests/${encodeURIComponent(requestId)}`, { signal }),
  comments: (requestId: string, targetId: string, cursor: string | null): Promise<HelpdeskCommentPage> =>
    req(`/api/external/helpdesk/requests/${encodeURIComponent(requestId)}/targets/${encodeURIComponent(targetId)}/comments${cursor ? `?${new URLSearchParams({ cursor })}` : ""}`),
  comment: (requestId: string, targetId: string, input: PostHelpdeskCommentInput, files: readonly File[], onProgress: (progress: number) => void): Promise<unknown> => {
    const path = `/api/external/helpdesk/requests/${encodeURIComponent(requestId)}/targets/${encodeURIComponent(targetId)}/comments`;
    return files.length
      ? multipartConversationRequest(
          path,
          input,
          files.map((file) => ({ fieldName: HELPDESK_ATTACHMENT_COMMENT_PART, file })),
          onProgress,
        )
      : req(path, { method: "POST", body: JSON.stringify(input) });
  },
  retry: (requestId: string, targetId: string): Promise<RetryHelpdeskRequestResponse> =>
    req(`/api/external/helpdesk/requests/${encodeURIComponent(requestId)}/targets/${encodeURIComponent(targetId)}/retry`, { method: "POST" }),
  category: (departmentId: string, signal?: AbortSignal): Promise<HelpdeskDepartmentForms> => req(`/api/external/helpdesk/catalog/categories/${encodeURIComponent(departmentId)}`, { signal }),
  form: (versionId: string, signal?: AbortSignal): Promise<HelpdeskExternalCatalogFormResponse> => req(`/api/external/helpdesk/catalog/forms/${encodeURIComponent(versionId)}`, { signal }),
  submit: (input: CreateHelpdeskRequestInput, attachments: readonly Attachment[], onProgress: (progress: number) => void): Promise<CreateHelpdeskRequestResponse> =>
    (attachments.length === 0
      ? req("/api/external/helpdesk/requests", { method: "POST", body: JSON.stringify(input) })
      : multipartConversationRequest(
          "/api/external/helpdesk/requests",
          input,
          attachments.map(({ fieldId, file }) => ({ fieldName: `${HELPDESK_ATTACHMENT_FORM_PART_PREFIX}${fieldId}`, file })),
          onProgress,
        )
    ).then((response) => confirmedSubmission(response, "/api/external/helpdesk/requests")),
};
